import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Loader2, X, AlertTriangle, CheckCircle2, MapPin } from "lucide-react";

/**
 * Phase 23.2 — AI-generated timesheet modal.
 *
 * Workflow:
 *  1. User (or supervisor) types a natural-language description of the
 *     target day.
 *  2. We call POST /api/pace/parse-timesheet — get back a list of
 *     draft proposals, each with an inline validation block.
 *  3. User edits, deletes, or fixes proposals. When every card is
 *     ok, they hit "Commit N entries" which POSTs commit-timesheet.
 *  4. Toast on each created entry; modal closes and parent re-fetches.
 */
export function AITimesheetModal({ open, onOpenChange, workers, defaultWorkerId, onCommitted }) {
  const [nl, setNl] = useState("");
  const [targetDate, setTargetDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [workerId, setWorkerId] = useState(defaultWorkerId || "");
  const [parsing, setParsing] = useState(false);
  const [parseResp, setParseResp] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [committing, setCommitting] = useState(false);
  const [rateLimited, setRateLimited] = useState(false);

  useEffect(() => {
    if (!open) {
      setNl("");
      setParseResp(null);
      setProposals([]);
      setRateLimited(false);
    }
  }, [open]);

  const validCount = useMemo(
    () => proposals.filter((p) => p.validation?.ok).length,
    [proposals]
  );

  const parse = async () => {
    if (nl.trim().length < 10) {
      toast.error("Please describe your day in at least 10 characters.");
      return;
    }
    setParsing(true);
    setRateLimited(false);
    try {
      const { data } = await api.post("/pace/parse-timesheet", {
        natural_language: nl,
        target_date: targetDate,
        worker_id: workerId || null,
      });
      setParseResp(data);
      setProposals(data.proposals || []);
      if ((data.proposals || []).length === 0 && data.rendered_by === "fallback") {
        toast(data.notes_from_pace || "Pace couldn't parse your day — try rephrasing.");
      }
    } catch (e) {
      if (e?.response?.status === 429) {
        setRateLimited(true);
      } else if (e?.response?.status === 503) {
        toast.error("Pace isn't turned on for this workspace.");
      } else {
        toast.error(formatApiError(e));
      }
    } finally { setParsing(false); }
  };

  const commit = async () => {
    if (validCount === 0) return;
    setCommitting(true);
    try {
      const body = {
        target_date: targetDate,
        worker_id: workerId || null,
        proposals: proposals
          .filter((p) => p.validation?.ok)
          .map((p) => ({
            worksite_id: p.worksite_id,
            in_time: p.in_time,
            out_time: p.out_time,
            paid_minutes: p.paid_minutes,
            notes: p.notes,
            dimension_values: p.dimension_values,
            task_id: p.task_id,
            confidence: p.confidence,
          })),
      };
      const { data } = await api.post("/pace/commit-timesheet", body);
      const n = (data.created || []).length;
      const skipped = (data.skipped || []).length;
      if (n > 0) toast.success(`Committed ${n} entr${n === 1 ? "y" : "ies"}.`);
      if (skipped > 0) toast(`${skipped} proposal${skipped === 1 ? "" : "s"} skipped due to validation errors.`);
      if (n > 0) {
        onCommitted?.(data.created);
        onOpenChange(false);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setCommitting(false); }
  };

  const updateProposal = (i, patch) => {
    setProposals((cur) => cur.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));
  };
  const removeProposal = (i) => {
    setProposals((cur) => cur.filter((_, idx) => idx !== i));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl" data-testid="ai-timesheet-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-2xl text-primary">
            <Sparkles className="h-5 w-5 text-secondary" />
            Generate my timesheet with Pace
          </DialogTitle>
          <DialogDescription>
            Describe your day in plain English — Pace will draft time entries you can review and commit.
            Your text is never stored beyond an anonymised audit hash.
          </DialogDescription>
        </DialogHeader>

        {rateLimited && (
          <div
            className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-900"
            data-testid="ai-timesheet-rate-limited"
          >
            You've used your Pace budget for today. Try again tomorrow.
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <Label htmlFor="ai-target-date">Target date</Label>
            <Input
              id="ai-target-date" type="date"
              value={targetDate} onChange={(e) => setTargetDate(e.target.value)}
              data-testid="ai-timesheet-target-date"
            />
          </div>
          {workers && workers.length > 0 && (
            <div className="sm:col-span-2">
              <Label htmlFor="ai-worker">Worker</Label>
              <select
                id="ai-worker"
                className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm"
                value={workerId || ""}
                onChange={(e) => setWorkerId(e.target.value)}
                data-testid="ai-timesheet-worker-picker"
              >
                <option value="">Myself</option>
                {workers.map((w) => (
                  <option key={w.id} value={w.id}>{w.full_name || w.email}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div>
          <Label htmlFor="ai-nl">What did you do?</Label>
          <Textarea
            id="ai-nl"
            value={nl} onChange={(e) => setNl(e.target.value)}
            placeholder="e.g. 'Worked at HQ from 9 to 11 on the HVAC project, then meetings until 3, lunch 12-12:30, ended at 5.'"
            maxLength={4000} rows={5}
            data-testid="ai-timesheet-nl-textarea"
          />
          <div className="mt-1 flex items-center justify-between text-xs text-muted-foreground">
            <span>Minimum 10 characters.</span>
            <span data-testid="ai-timesheet-char-count">{nl.length} / 4000</span>
          </div>
        </div>

        {parseResp?.unresolved_mentions?.length > 0 && (
          <div className="rounded-md border border-amber-400/40 bg-amber-50 p-3" data-testid="ai-timesheet-unresolved">
            <div className="mb-2 text-xs font-semibold uppercase tracking-widest text-amber-800">
              Pace couldn't map:
            </div>
            <div className="flex flex-wrap gap-1.5">
              {parseResp.unresolved_mentions.map((m, i) => (
                <Badge key={i} variant="outline" className="border-amber-500/60 bg-white text-xs text-amber-900">
                  {m}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {parseResp?.notes_from_pace && (
          <div className="flex items-start gap-2 rounded-lg bg-secondary/10 p-3" data-testid="ai-timesheet-notes">
            <Sparkles className="mt-0.5 h-4 w-4 text-secondary" />
            <div className="text-sm text-primary">{parseResp.notes_from_pace}</div>
          </div>
        )}

        {proposals.length > 0 && (
          <div className="max-h-96 space-y-2 overflow-y-auto pr-1" data-testid="ai-timesheet-proposals">
            {proposals.map((p, i) => (
              <div
                key={i}
                className={`rounded-lg border p-3 ${p.validation?.ok ? "border-emerald-500/40 bg-emerald-50/40" : "border-red-500/40 bg-red-50/40"}`}
                data-testid={`ai-timesheet-proposal-${i}`}
              >
                <div className="mb-2 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                    <b>{p.worksite_name || p.worksite_id}</b>
                    <Badge variant="outline" className="text-[10px]">
                      confidence {Math.round((p.confidence || 0) * 100)}%
                    </Badge>
                    {p.validation?.ok ? (
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                    ) : (
                      <AlertTriangle className="h-3.5 w-3.5 text-red-600" />
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeProposal(i)}
                    className="grid h-6 w-6 place-items-center rounded hover:bg-black/5"
                    data-testid={`ai-timesheet-proposal-remove-${i}`}
                    title="Remove from batch"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>

                <div className="grid grid-cols-1 gap-2 sm:grid-cols-4">
                  <div>
                    <Label htmlFor={`ai-in-${i}`} className="text-xs">In</Label>
                    <Input
                      id={`ai-in-${i}`} type="datetime-local"
                      value={(p.in_time || "").slice(0, 16)}
                      onChange={(e) => updateProposal(i, { in_time: e.target.value + ":00" + (p.in_time?.slice(-6) || "+00:00") })}
                      data-testid={`ai-proposal-in-${i}`}
                    />
                  </div>
                  <div>
                    <Label htmlFor={`ai-out-${i}`} className="text-xs">Out</Label>
                    <Input
                      id={`ai-out-${i}`} type="datetime-local"
                      value={(p.out_time || "").slice(0, 16)}
                      onChange={(e) => updateProposal(i, { out_time: e.target.value + ":00" + (p.out_time?.slice(-6) || "+00:00") })}
                      data-testid={`ai-proposal-out-${i}`}
                    />
                  </div>
                  <div className="text-xs text-muted-foreground sm:col-span-2">
                    <Label className="text-xs">Notes</Label>
                    <Input
                      value={p.notes || ""}
                      onChange={(e) => updateProposal(i, { notes: e.target.value })}
                      placeholder="Optional notes"
                      data-testid={`ai-proposal-notes-${i}`}
                    />
                  </div>
                </div>

                {p.reasoning && (
                  <div className="mt-2 text-xs italic text-muted-foreground">
                    Pace: “{p.reasoning}”
                  </div>
                )}

                {p.validation && !p.validation.ok && (
                  <div
                    className="mt-2 rounded bg-red-100 p-2 text-xs text-red-900"
                    data-testid={`ai-proposal-errors-${i}`}
                  >
                    {p.validation.errors.map((err, ei) => (
                      <div key={ei}>• {err}</div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <DialogFooter className="mt-2 flex items-center justify-between gap-3">
          {parseResp ? (
            <div className="text-xs text-muted-foreground">
              {validCount} / {proposals.length} ready to commit
              {parseResp.rendered_by === "fallback" && " · fallback mode"}
            </div>
          ) : (
            <div />
          )}
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="ai-timesheet-cancel">
              Cancel
            </Button>
            {!parseResp ? (
              <Button onClick={parse} disabled={parsing || nl.trim().length < 10} data-testid="ai-timesheet-parse">
                {parsing ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Parsing…</> : "Parse with Pace"}
              </Button>
            ) : (
              <Button
                onClick={commit}
                disabled={committing || validCount === 0}
                data-testid="ai-timesheet-commit"
              >
                {committing ? "Committing…" : `Commit ${validCount} entr${validCount === 1 ? "y" : "ies"}`}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Trigger button — call it wherever you want the modal to launch from. */
export function AITimesheetTriggerButton({ workers, defaultWorkerId, onCommitted }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        variant="outline"
        className="gap-1.5 border-secondary/40 text-secondary hover:bg-secondary/10"
        onClick={() => setOpen(true)}
        data-testid="ai-timesheet-open-btn"
      >
        <Sparkles className="h-4 w-4" /> Generate with Pace
      </Button>
      <AITimesheetModal
        open={open} onOpenChange={setOpen}
        workers={workers} defaultWorkerId={defaultWorkerId}
        onCommitted={onCommitted}
      />
    </>
  );
}
